import { z } from 'zod';
/**
 * Contrat d'ingestion des enregistrements : ce que le SDK envoie, ce que l'API accepte.
 *
 * Un batch regroupe les événements rrweb d'**une window** (un onglet) d'une session. Il est
 * identifié par `(sessionId, windowId, seq)` : le renvoyer (retry, onglet rechargé…) ne doit rien
 * changer côté serveur. `seq` est monotone par window, à partir de 0.
 */
/** Chemin de l'endpoint d'ingestion, relatif à l'hôte de l'API (`apiHost`). */
export const RECORDINGS_PATH = '/ingest/recordings';
/** Taille maximale d'un batch, décompressé (cf. PRD : 413 au-delà). */
export const MAX_BATCH_BYTES = 5 * 1024 * 1024;
/**
 * Corps compressé en gzip, annoncé dans l'URL (`?compression=gzip`) et non par un en-tête
 * `Content-Encoding` : la requête reste « simple » au sens CORS (pas de requête préalable), ce
 * qu'exigent les envois `keepalive` à la fermeture de l'onglet.
 */
export const COMPRESSION_PARAM = 'compression';
export const GZIP_COMPRESSION = 'gzip';
/** Volume maximal cumulé d'une session, décompressé (cf. PRD : 413 `session_too_large`). */
export const MAX_SESSION_BYTES = 100 * 1024 * 1024;
/** Code d'erreur de la réponse 413 : le SDK arrête d'enregistrer la session. */
export const SESSION_TOO_LARGE = 'session_too_large';
/**
 * Code d'erreur de la réponse 403 : la page qui envoie (en-tête `Origin`) ne figure pas parmi
 * les origines autorisées du projet. Le SDK cesse d'enregistrer cette page.
 */
export const ORIGIN_NOT_ALLOWED = 'origin_not_allowed';
/**
 * Événement rrweb : seuls `type` et `timestamp` sont contrôlés, `data` est conservé tel quel
 * (le format rrweb évolue avec ses versions et n'est relu que par le lecteur).
 */
export const rrwebEventSchema = z.looseObject({
    type: z.number().int().min(0).max(255),
    timestamp: z.number().int().nonnegative(),
    data: z.unknown(),
});
/** Contexte de l'onglet au moment de l'envoi. */
export const batchContextSchema = z.strictObject({
    url: z.string().max(4096),
    referrer: z.string().max(4096),
    userAgent: z.string().max(1024),
    locale: z.string().max(64),
    timezone: z.string().max(64),
    viewport: z.strictObject({
        width: z.number().int().nonnegative(),
        height: z.number().int().nonnegative(),
    }),
});
/**
 * Identifiant d'une person dans le système du client (son `userId`), pris tel quel : c'est
 * l'`externalId` de l'API serveur des persons.
 */
export const externalIdSchema = z.string().min(1).max(200);
/** Nom réservé de l'event qui rattache le `distinctId` du batch à une person (cf. PRD). */
export const IDENTIFY_EVENT = '$identify';
/**
 * `$identify` : le navigateur (`distinctId` du batch) appartient désormais à la person
 * `userId`, créée si elle n'existe pas. Le renvoyer ne change rien.
 */
export const identifyEventSchema = z.strictObject({
    /** Identifiant de l'event (UUIDv7), pour le dédoublonnage. */
    uuid: z.uuid(),
    name: z.literal(IDENTIFY_EVENT),
    timestamp: z.number().int().nonnegative(),
    properties: z.strictObject({ userId: externalIdSchema }),
});
/** Longueur maximale du nom d'un event custom. */
export const MAX_EVENT_NAME_LENGTH = 200;
/** Taille maximale des propriétés d'un event custom, une fois sérialisées en JSON (caractères). */
export const MAX_EVENT_PROPERTIES_LENGTH = 10_000;
/** Events custom au plus par batch : le SDK envoie sans attendre quand il les atteint. */
export const MAX_CUSTOM_EVENTS_PER_BATCH = 100;
/**
 * Nom d'un event de `capture(name, properties)` : 1 à 200 caractères ; les noms commençant par
 * `$` sont réservés à belvdr (`$identify`).
 */
export const captureEventNameSchema = z
    .string()
    .min(1)
    .max(MAX_EVENT_NAME_LENGTH)
    .refine((name) => !name.startsWith('$'), 'Les noms commençant par « $ » sont réservés.');
/**
 * Event de `capture(name, properties)` : un moment clé du parcours, affiché sur la timeline du
 * lecteur. Propriétés : un objet JSON, 10 000 caractères au plus une fois sérialisé.
 */
export const captureEventSchema = z.strictObject({
    /** Identifiant de l'event (UUIDv7) : un event renvoyé (retry) n'est enregistré qu'une fois. */
    uuid: z.uuid(),
    name: captureEventNameSchema,
    timestamp: z.number().int().nonnegative(),
    properties: z
        .record(z.string(), z.json())
        .refine((properties) => JSON.stringify(properties).length <= MAX_EVENT_PROPERTIES_LENGTH, `Propriétés trop volumineuses (${MAX_EVENT_PROPERTIES_LENGTH} caractères au plus en JSON).`),
    /** URL de la page au moment de l'event. */
    url: z.string().max(4096),
});
/** Events custom portés par un batch : `$identify` et les events de `capture()`. */
export const customEventSchema = z.union([identifyEventSchema, captureEventSchema]);
export const recordingBatchSchema = z.strictObject({
    /** Clé publique du projet (`pk_…`) : dans le corps pour rester compatible avec `keepalive`. */
    publicKey: z.string().regex(/^pk_[A-Za-z0-9_-]{1,100}$/),
    sdkVersion: z.string().min(1).max(32),
    /** Identifiant du navigateur (UUIDv7). */
    distinctId: z.uuid(),
    sessionId: z.uuid(),
    /** Début de la session, en ms depuis l'epoch : identique pour tous les batchs d'une session. */
    sessionStartedAt: z.number().int().nonnegative(),
    windowId: z.uuid(),
    /**
     * Onglet du navigateur où la page est chargée (UUIDv7) : le même pour ses rechargements et
     * navigations, un autre pour chaque onglet. Absent des batchs d'un SDK plus ancien.
     */
    tabId: z.uuid().optional(),
    seq: z.number().int().min(0).max(4_294_967_295),
    context: batchContextSchema,
    events: z.array(rrwebEventSchema).min(1),
    /** Events custom survenus pendant ce batch (absent s'il n'y en a pas). */
    customEvents: z.array(customEventSchema).min(1).max(MAX_CUSTOM_EVENTS_PER_BATCH).optional(),
});
export function isIdentifyEvent(event) {
    return event.name === IDENTIFY_EVENT;
}
